import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Copy, FileDown, Pencil, Plus, Search, Trash2, ClipboardList } from 'lucide-react'
import toast from 'react-hot-toast'

import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { TableColumnVisibilityMenu, initialColumnVisibility, countVisibleColumns } from '@/components/shared/TableColumnVisibilityMenu'
import { showDeleteConfirm, showSuccess } from '@/lib/swal'
import { useAuthStore } from '@/store/auth.store'
import { useSettingsStore } from '@/store/settings.store'
import { employeesService } from '@/services/employeesService'
import { purchaseRequisitionService } from '@/services/purchaseRequisitionService'

import type { DepartmentLookup } from '@/api/types'
import type { PurchaseRequisitionListRow } from '@/services/purchaseRequisitionService'

const PAGE_SIZE = 10

type ColumnKey = 'code' | 'date' | 'department' | 'estimatedDeliveryDate' | 'status'

const ALL_COLUMNS: { key: ColumnKey; labelKey: string }[] = [
  { key: 'code', labelKey: 'purchaseRequisitions.columns.code' },
  { key: 'date', labelKey: 'purchaseRequisitions.columns.date' },
  { key: 'department', labelKey: 'purchaseRequisitions.columns.department' },
  { key: 'estimatedDeliveryDate', labelKey: 'purchaseRequisitions.columns.estimatedDeliveryDate' },
  { key: 'status', labelKey: 'purchaseRequisitions.columns.status' },
]

function fmtDate(value?: string, lang?: string) {
  if (!value) return '—'
  const d = new Date(`${value}T12:00:00`)
  if (Number.isNaN(d.getTime())) return value
  const locale = lang?.startsWith('ar') ? 'ar-SA' : 'en-GB'
  return d.toLocaleDateString(locale)
}

export const PurchaseRequisitionsList: React.FC = () => {
  const { t, i18n } = useTranslation('common')
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const { language = 'en' } = useSettingsStore()
  const branchId = user?.selectedBranch?.branchId

  const [searchQuery, setSearchQuery] = useState('')
  const [visibleColumns, setVisibleColumns] = useState<Record<string, boolean>>(
    initialColumnVisibility(ALL_COLUMNS.map((c) => c.key)),
  )
  const [loading, setLoading] = useState(false)
  const [pageNumber, setPageNumber] = useState(1)
  const [rows, setRows] = useState<PurchaseRequisitionListRow[]>([])
  const [departments, setDepartments] = useState<DepartmentLookup[]>([])

  useEffect(() => {
    if (!branchId) return
    const load = async () => {
      try {
        setLoading(true)
        const data = await purchaseRequisitionService.getAll(branchId, pageNumber, PAGE_SIZE)
        setRows(data.items)
      } catch (error: any) {
        const status = error.response?.status
        const msg = (error.response?.data?.message || '').toLowerCase()
        const isNoData = status === 404 || /not found|no data|no records/.test(msg)
        setRows([])
        if (!isNoData) {
          toast.error(error.response?.data?.message || t('purchaseRequisitions.emptyTable'))
        }
      } finally {
        setLoading(false)
      }
    }
    void load()
  }, [branchId, pageNumber, t])

  useEffect(() => {
    const loadDepartments = async () => {
      try {
        const deps = await employeesService.getDepartmentLookups()
        setDepartments(deps || [])
      } catch {
        setDepartments([])
      }
    }
    void loadDepartments()
  }, [])

  const departmentById = useMemo(() => {
    const map: Record<number, string> = {}
    departments.forEach((d) => {
      map[d.id] = language === 'ar' ? d.lookupNameAr || d.lookupNameEn : d.lookupNameEn || d.lookupNameAr
    })
    return map
  }, [departments, language])

  const filtered = useMemo(() => {
    const q = searchQuery.trim().toLowerCase()
    if (!q) return rows
    return rows.filter((r) =>
      [
        r.code,
        r.date,
        r.estimatedDeliveryDate,
        r.reference,
        r.departmentName,
        departmentById[r.departmentId] || String(r.departmentId || ''),
      ].some((f) => String(f ?? '').toLowerCase().includes(q)),
    )
  }, [rows, searchQuery, departmentById])

  const pageSlice = searchQuery.trim() ? filtered.slice(0, PAGE_SIZE) : filtered

  const toggleColumn = (id: string) => {
    const key = id as ColumnKey
    if (!ALL_COLUMNS.some((c) => c.key === key)) return
    setVisibleColumns((prev) => ({ ...prev, [key]: !prev[key] }))
  }

  const columnMenuItems = useMemo(
    () => ALL_COLUMNS.map((c) => ({ id: c.key, label: t(c.labelKey) })),
    [t],
  )

  const visibleCount = countVisibleColumns(visibleColumns)
  const tableColSpan = visibleCount + 1

  const resolveStatusText = (statusCode: number) => {
    if (statusCode === 434) return t('purchaseRequisitions.status.Pending', { defaultValue: 'Pending' })
    if (statusCode === 435) return t('purchaseRequisitions.status.Approved', { defaultValue: 'Approved' })
    if (statusCode === 436) return t('purchaseRequisitions.status.Rejected', { defaultValue: 'Rejected' })
    if (!statusCode) return t('purchaseRequisitions.status.Draft', { defaultValue: 'Draft' })
    return String(statusCode)
  }

  const statusBadge = (statusCode: number) => {
    const text = resolveStatusText(statusCode)
    const isApproved = statusCode === 435
    const isPending = statusCode === 434
    return (
      <Badge variant={isApproved ? 'default' : isPending ? 'secondary' : 'outline'} className="text-xs">
        {text}
      </Badge>
    )
  }

  const handleCopy = () => {
    if (filtered.length === 0) return toast.error(t('purchaseRequisitions.nothingToCopy'))
    const header = ALL_COLUMNS.filter((c) => visibleColumns[c.key]).map((c) => t(c.labelKey))
    const lines = filtered.map((r) => {
      const cells: string[] = []
      if (visibleColumns.code) cells.push(r.code)
      if (visibleColumns.date) cells.push(r.date)
      if (visibleColumns.department) cells.push(departmentById[r.departmentId] || r.departmentName || String(r.departmentId || ''))
      if (visibleColumns.estimatedDeliveryDate) cells.push(r.estimatedDeliveryDate)
      if (visibleColumns.status) cells.push(resolveStatusText(Number((r as any).status ?? 0)))
      return cells.join('\t')
    })
    void navigator.clipboard.writeText([header.join('\t'), ...lines].join('\n'))
    toast.success(t('purchaseRequisitions.copied'))
  }

  const csvEscape = (cell: string) => `"${cell.replace(/"/g, '""')}"`

  const handleExportCSV = () => {
    if (filtered.length === 0) return toast.error(t('purchaseRequisitions.nothingToExport'))
    const header = ALL_COLUMNS.filter((c) => visibleColumns[c.key])
      .map((c) => csvEscape(t(c.labelKey)))
      .join(',')
    const body = pageSlice
      .map((r) => {
        const cells: string[] = []
        if (visibleColumns.code) cells.push(r.code)
        if (visibleColumns.date) cells.push(r.date)
        if (visibleColumns.department) cells.push(departmentById[r.departmentId] || r.departmentName || String(r.departmentId || ''))
        if (visibleColumns.estimatedDeliveryDate) cells.push(r.estimatedDeliveryDate)
        if (visibleColumns.status) cells.push(resolveStatusText(Number((r as any).status ?? 0)))
        return cells.map(csvEscape).join(',')
      })
      .join('\n')
    const blob = new Blob([`${header}\n${body}`], { type: 'text/csv' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `purchase_requisitions_${new Date().toISOString().split('T')[0]}.csv`
    a.click()
    window.URL.revokeObjectURL(url)
    toast.success(t('purchaseRequisitions.exported'))
  }

  const handleDelete = async (r: PurchaseRequisitionListRow) => {
    const ok = await showDeleteConfirm(r.code)
    if (!ok) return
    try {
      await purchaseRequisitionService.delete(r.id)
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('common.failed'))
      return
    }
    await showSuccess(t('common.success'), t('purchaseRequisitions.deleted'))
    setPageNumber(1)
  }

  return (
    <div className="space-y-6 p-4 sm:p-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <ClipboardList className="h-5 w-5" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">{t('purchaseRequisitions.title')}</h1>
            <p className="text-sm text-muted-foreground">{t('purchaseRequisitions.listSubtitle')}</p>
          </div>
        </div>

        <Button onClick={() => navigate('/supply-chain/purchase-requisitions/new')} className="w-full sm:w-auto shrink-0">
          <Plus className="mr-2 h-4 w-4" />
          {t('purchaseRequisitions.add')}
        </Button>
      </div>

      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-end">
            <div className="flex-1">
              <label className="mb-2 block text-sm font-medium">{t('purchaseRequisitions.search')}</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder={t('purchaseRequisitions.searchPlaceholder')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              <TableColumnVisibilityMenu
                columns={columnMenuItems}
                visibility={visibleColumns}
                onToggle={toggleColumn}
                triggerLabel={t('purchaseRequisitions.columnVisibility')}
                menuTitle={t('purchaseRequisitions.columnsToggle')}
              />
              <Button variant="outline" size="sm" onClick={handleExportCSV}>
                <FileDown className="mr-2 h-4 w-4" />
                CSV
              </Button>
              <Button variant="outline" size="sm" onClick={handleCopy}>
                <Copy className="mr-2 h-4 w-4" />
                {t('purchaseRequisitions.copy')}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-b-2 border-primary/20 hover:bg-transparent">
                  {visibleColumns.code && (
                    <TableHead className="h-12 bg-primary/5 px-4 text-center font-bold text-primary">
                      {t('purchaseRequisitions.columns.code')}
                    </TableHead>
                  )}
                  {visibleColumns.date && (
                    <TableHead className="h-12 bg-primary/5 px-4 text-center font-bold text-primary">
                      {t('purchaseRequisitions.columns.date')}
                    </TableHead>
                  )}
                  {visibleColumns.department && (
                    <TableHead className="h-12 bg-primary/5 px-4 text-center font-bold text-primary">
                      {t('purchaseRequisitions.columns.department')}
                    </TableHead>
                  )}
                  {visibleColumns.estimatedDeliveryDate && (
                    <TableHead className="h-12 bg-primary/5 px-4 text-center font-bold text-primary">
                      {t('purchaseRequisitions.columns.estimatedDeliveryDate')}
                    </TableHead>
                  )}
                  {visibleColumns.status && (
                    <TableHead className="h-12 bg-primary/5 px-4 text-center font-bold text-primary">
                      {t('purchaseRequisitions.columns.status')}
                    </TableHead>
                  )}
                  <TableHead className="h-12 w-[110px] bg-primary/5 px-2 text-center font-bold text-primary">
                    {t('purchaseRequisitions.columns.actions')}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={tableColSpan} className="py-12 text-center text-muted-foreground">
                      <div className="p-12 text-center">
                        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
                        <p className="mt-4 text-muted-foreground">{t('common.loading')}</p>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : pageSlice.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={tableColSpan} className="py-12 text-center text-muted-foreground">
                      {t('purchaseRequisitions.emptyTable')}
                    </TableCell>
                  </TableRow>
                ) : (
                  pageSlice.map((r) => (
                    <TableRow key={r.id} className="border-b transition-colors hover:bg-primary/5">
                      {visibleColumns.code && <TableCell className="px-4 py-2 text-center text-sm">{r.code}</TableCell>}
                      {visibleColumns.date && (
                        <TableCell className="px-4 py-2 text-center text-sm">{fmtDate(r.date, i18n.language)}</TableCell>
                      )}
                      {visibleColumns.department && (
                        <TableCell className="px-4 py-2 text-center text-sm">
                          {departmentById[r.departmentId] || r.departmentName || r.departmentId || '—'}
                        </TableCell>
                      )}
                      {visibleColumns.estimatedDeliveryDate && (
                        <TableCell className="px-4 py-2 text-center text-sm">
                          {fmtDate(r.estimatedDeliveryDate, i18n.language)}
                        </TableCell>
                      )}
                      {visibleColumns.status && (
                        <TableCell className="px-4 py-2 text-center align-middle">{statusBadge(Number((r as any).status ?? 0))}</TableCell>
                      )}
                      <TableCell className="px-2 py-2 text-center">
                        <div className="flex items-center justify-center gap-0.5">
                          {(() => {
                            const statusCode = Number((r as any).status ?? 0)
                            const isApproved = statusCode === 435
                            return (
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                disabled={isApproved}
                                className={
                                  isApproved
                                    ? 'h-8 w-8 p-0 opacity-40 cursor-not-allowed'
                                    : 'h-8 w-8 p-0 hover:bg-primary/10'
                                }
                                title={
                                  isApproved
                                    ? t('purchaseRequisitions.editDisabledApproved', {
                                        defaultValue: 'Approved requisitions cannot be edited.',
                                      })
                                    : t('purchaseRequisitions.edit')
                                }
                                aria-label={t('purchaseRequisitions.edit')}
                                onClick={() => {
                                  if (isApproved) return
                                  navigate(`/supply-chain/purchase-requisitions/${r.id}/edit`)
                                }}
                              >
                                <Pencil className="h-4 w-4" />
                              </Button>
                            )
                          })()}
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0 text-destructive hover:bg-destructive/10 hover:text-destructive"
                            title={t('purchaseRequisitions.delete')}
                            aria-label={t('purchaseRequisitions.delete')}
                            onClick={() => void handleDelete(r)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

